/**
 * Everything you have made, on one shelf.
 *
 * There was no such screen because there was no such noun. A page lived in
 * /pages, a briefing inside the beat that produced it, and a brainstorm report
 * nowhere at all — three lifecycles with no shared word between them, so "what
 * have I got, and what needs me" had to be answered by opening three features
 * and holding the result in your head.
 *
 * THE STATE IS THE POINT, not the kind. What a person wants from this screen is
 * the work that is stuck: drafts nobody finished, things cleared to ship that
 * nobody shipped, runs that failed. So the state chips lead and carry counts,
 * and the kind filter is secondary — you come here asking "what needs me",
 * rarely "show me briefings".
 *
 * Counts come from the server over the WHOLE shelf, so narrowing to Ready does
 * not rewrite every chip to the rows already on screen.
 */
import { useCallback, useEffect, useState, useMemo } from 'react';
import PageHeader from '../components/common/PageHeader';
import { useSearchParams, useLocation } from 'react-router-dom';
import ShellLink from '../components/common/ShellLink';
import api from '../services/api';
import { useLiveReload, usePollWhile } from '../components/common/useLiveReload';
import { withReturnTo } from '../components/common/returnPath';
import type { ArtifactKind, ArtifactListResponse, ArtifactState, ArtifactSummary } from '../types';
import { groupArtifacts, searchArtifacts } from './artifactGroups';
import ShareArtifactControl from '../components/common/ShareArtifactControl';
import type { GroupBy } from './artifactGroups';

/**
 * Which kinds can be shared, and which id the control wants for each.
 *
 * Mirrors MINTABLE in api/artifacts.py. A landing page is deliberately absent —
 * it is reserved in the enum with nothing behind it yet, and offering a share
 * button that 400s is worse than offering none.
 */
const SHAREABLE: Partial<
  Record<ArtifactKind, (id: number) => { kind: ArtifactKind; [k: string]: unknown }>
> = {
  NEWS_BRIEFING: (id) => ({ kind: 'NEWS_BRIEFING' as ArtifactKind, newsBriefingId: id }),
  MANAGED_PAGE: (id) => ({ kind: 'MANAGED_PAGE' as ArtifactKind, managedPageId: id }),
  BOARDROOM_REPORT: (id) => ({
    kind: 'BOARDROOM_REPORT' as ArtifactKind,
    boardroomId: id,
  }),
  BOARDROOM_CARD: (id) => ({
    kind: 'BOARDROOM_CARD' as ArtifactKind,
    boardroomDeliverableId: id,
  }),
};

const STATE_ORDER: ArtifactState[] = ['BUILDING', 'DRAFT', 'READY', 'PUBLISHED', 'FAILED'];

const STATE_LABEL: Record<ArtifactState, string> = {
  BUILDING: 'Building',
  DRAFT: 'Draft',
  READY: 'Ready',
  PUBLISHED: 'Published',
  FAILED: 'Failed',
};

/** Form carries the state as well as the word, so what needs attention reads at
 *  a glance rather than by reading every row. */
const STATE_PILL: Record<ArtifactState, string> = {
  BUILDING:
    'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800',
  DRAFT:
    'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600',
  READY:
    'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800',
  PUBLISHED:
    'bg-green-50 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800',
  FAILED:
    'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800',
};

const KIND_LABEL: Record<string, string> = {
  MANAGED_PAGE: 'Page',
  NEWS_BRIEFING: 'Briefing',
  BOARDROOM_REPORT: 'Digest',
  BOARDROOM_CARD: 'Report',
  LANDING_PAGE: 'Landing page',
};

function timeAgo(value?: string | null): string {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(value).toLocaleDateString();
}

export function StatePill({ state }: { state: ArtifactState }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${STATE_PILL[state]}`}
    >
      {STATE_LABEL[state]}
    </span>
  );
}

export default function Artifacts() {
  const [data, setData] = useState<ArtifactListResponse | null>(null);
  // Filters live in the URL so Home can send you straight to "the 3 things
  // waiting to ship" — a count you can't click is a count you have to go and
  // find by hand.
  const [searchParams, setSearchParams] = useSearchParams();
  const state = (searchParams.get('state') as ArtifactState | null) || null;
  const kind = searchParams.get('kind');
  // In the URL so a grouped, searched view is linkable — "here is everything
  // Trucking has produced" is a thing worth sending someone.
  const groupBy = (searchParams.get('group') as GroupBy | null) ?? 'subject';
  const query = searchParams.get('q') ?? '';
  // Where it came FROM, where it GOES, and where it is FILED. Three different
  // questions that used to have one answer between them — the subject an artifact
  // came from was the only thing the shelf knew, and it stopped being the same
  // as where the artifact publishes the moment paths became editable.
  const subject = searchParams.get('subject');
  const site = searchParams.get('site');
  // `''` is a real selection here, not "unset": it means UNFILED. `null` is
  // "not asked". Collapsing them would hide the artifacts with no delivery path,
  // which are the ones worth finding.
  const path = searchParams.get('path');

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };
  const setState = (v: ArtifactState | null) => setParam('state', v);
  const setKind = (v: string | null) => setParam('kind', v);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(
        await api.listArtifacts({
          state: state ?? undefined,
          kind: kind ?? undefined,
          subject: subject ?? undefined,
          site: site ?? undefined,
          // Pass through even when empty — see the note on `path` above.
          path: path ?? undefined,
        }),
      );
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load artifacts');
    } finally {
      setLoading(false);
    }
  }, [state, kind, subject, site, path]);

  useEffect(() => {
    load();
  }, [load]);
  // THIS PAGE IS ABOUT STATES, and they change in the background: a build
  // finishing turns BUILDING into DRAFT or FAILED, a deploy into PUBLISHED.
  // Re-read when a build ends, and every 10 seconds while anything is building.
  useLiveReload(
    (e) => e?.event_type === 'page_build' && ['completed', 'failed', 'error', 'cancelled'].includes(e.status),
    () => void load(),
  );
  usePollWhile((data?.state_counts?.BUILDING ?? 0) > 0, () => void load(), 10_000);

  const stateCounts = data?.state_counts ?? {};
  const kindCounts = data?.kind_counts ?? {};
  const pathCounts = data?.path_counts ?? {};
  const subjectCounts = data?.subject_counts ?? {};
  const siteCounts = data?.site_counts ?? {};
  const grandTotal = Object.values(stateCounts).reduce((a, b) => a + b, 0);
  const all = data?.items ?? [];
  // Search is client-side ON PURPOSE: the shelf is already fetched whole so the
  // state chips can count it, and a round trip per keystroke would make the one
  // control people use most the slowest.
  const items = useMemo(() => searchArtifacts(all, query), [all, query]);
  const groups = useMemo(() => groupArtifacts(items, groupBy), [items, groupBy]);
  const filtered =
    state !== null ||
    kind !== null ||
    subject !== null ||
    site !== null ||
    path !== null ||
    query.trim() !== '';

  return (
    <div>
      <PageHeader
        title="Artifacts"
        subtitle="Everything you’ve produced - pages, briefings and recon digests - and where each one has got to."
        className="mb-5"
      />

      {error && (
        <div className="mb-4 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
          {error}
        </div>
      )}

      {/* State first: you arrive asking what needs you, not what kind it is. */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button
          onClick={() => setState(null)}
          className={`px-2.5 py-1 rounded-md text-sm border ${
            state === null
              ? 'bg-gray-900 text-white border-gray-900 dark:bg-gray-100 dark:text-gray-900 dark:border-gray-100'
              : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          All {grandTotal}
        </button>
        {STATE_ORDER.filter((s) => (stateCounts[s] ?? 0) > 0).map((s) => (
          <button
            key={s}
            onClick={() => setState(state === s ? null : s)}
            className={`px-2.5 py-1 rounded-md text-sm border ${
              state === s
                ? 'bg-gray-900 text-white border-gray-900 dark:bg-gray-100 dark:text-gray-900 dark:border-gray-100'
                : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {STATE_LABEL[s]} {stateCounts[s]}
          </button>
        ))}

        <div className="flex-1" />

        <input
          type="search"
          value={query}
          onChange={(e) => setParam('q', e.target.value || null)}
          placeholder="Find by name or subject…"
          aria-label="Find artifacts"
          className="w-56 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-1 text-sm"
        />

        <select
          aria-label="Group by"
          value={groupBy}
          onChange={(e) => setParam('group', e.target.value)}
          className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-1 text-sm"
        >
          <option value="subject">Group by subject</option>
          <option value="kind">Group by kind</option>
          <option value="none">No grouping</option>
        </select>

        {/* Only offered once there is more than one kind to choose between — an
            empty-feeling dropdown teaches people to ignore the bar. */}
        {Object.keys(kindCounts).length > 1 && (
          <select
            aria-label="Filter by kind"
            value={kind ?? ''}
            onChange={(e) => setKind(e.target.value || null)}
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-1 text-sm"
          >
            <option value="">All kinds</option>
            {Object.entries(kindCounts).map(([k, n]) => (
              <option key={k} value={k}>
                {KIND_LABEL[k] ?? k} ({n})
              </option>
            ))}
          </select>
        )}

        {/* Each offered only once it can distinguish anything. A dropdown with
            one option teaches people the bar is decorative. */}
        {Object.keys(subjectCounts).length > 1 && (
          <select
            aria-label="Filter by subject"
            value={subject ?? ''}
            onChange={(e) => setParam('subject', e.target.value || null)}
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-1 text-sm"
          >
            <option value="">All subjects</option>
            {Object.entries(subjectCounts).map(([k, n]) => (
              <option key={k} value={k}>
                {k} ({n})
              </option>
            ))}
          </select>
        )}

        {Object.keys(siteCounts).length > 1 && (
          <select
            aria-label="Filter by site"
            value={site ?? ''}
            onChange={(e) => setParam('site', e.target.value || null)}
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-1 text-sm"
          >
            <option value="">All sites</option>
            {Object.entries(siteCounts).map(([k, n]) => (
              <option key={k} value={k}>
                {k} ({n})
              </option>
            ))}
          </select>
        )}

        {Object.keys(pathCounts).length > 0 && (
          <select
            aria-label="Filter by publish path"
            // `__unfiled__` rather than '': an empty option value is
            // indistinguishable from "nothing selected" in a native select,
            // and unfiled is a selection people need to be able to make.
            value={path === null ? '' : path === '' ? '__unfiled__' : path}
            onChange={(e) => {
              const v = e.target.value;
              setParam('path', v === '' ? null : v === '__unfiled__' ? '' : v);
            }}
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-1 text-sm font-mono"
          >
            <option value="">Any path</option>
            <option value="__unfiled__">Not on a site</option>
            {Object.entries(pathCounts).map(([k, n]) => (
              <option key={k} value={k}>
                /{k}/ ({n})
              </option>
            ))}
          </select>
        )}
      </div>

      {loading && items.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-gray-400 py-8">Loading…</div>
      ) : grandTotal === 0 ? (
        <div className="text-center py-16 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <p className="text-gray-600 dark:text-gray-300">Nothing made yet.</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Build a page, run a news beat, or share a recon digest and it will show
            up here.
          </p>
        </div>
      ) : items.length === 0 ? (
        /* Filtered to nothing is a different state from having nothing, and it
           needs a way back — an empty list under an active filter reads as
           "your work is gone". */
        <div className="text-center py-12 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Nothing matches these filters.
          </p>
          <button
            onClick={() => {
              // Every narrowing control, not just the chips — searching to
              // nothing and then clicking "clear filters" left you exactly
              // where you were, which reads as a dead button.
              const next = new URLSearchParams(searchParams);
              next.delete('state');
              next.delete('kind');
              next.delete('q');
              // Every facet, or "clear filters" leaves the list looking
              // filtered-to-nothing and the button looking broken.
              next.delete('subject');
              next.delete('site');
              next.delete('path');
              setSearchParams(next, { replace: true });
            }}
            className="mt-2 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            Clear filters - show all {grandTotal}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((g) => (
            <div key={g.key}>
              {g.label && (
                <h2 className="mb-1.5 flex items-baseline gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
                  {g.label}
                  <span className="text-xs font-normal text-gray-400 dark:text-gray-500">
                    {g.items.length}
                  </span>
                </h2>
              )}
              <ul className="flex flex-col divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden">
                {g.items.map((a) => (
                  <ArtifactRow key={`${a.kind}-${a.id}`} artifact={a} />
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {filtered && items.length > 0 && (
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          Showing {items.length.toLocaleString()} of {grandTotal.toLocaleString()}.
        </p>
      )}
    </div>
  );
}

function ArtifactRow({ artifact }: { artifact: ArtifactSummary }) {
  const location = useLocation();
  const body = (
    <div className="flex items-center gap-3 px-4 py-3">
      <span className="text-[11px] uppercase tracking-wider text-gray-400 dark:text-gray-500 w-16 shrink-0">
        {KIND_LABEL[artifact.kind] ?? artifact.kind}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
          {artifact.title}
        </span>
        {(artifact.source || artifact.project_name) && (
          <span className="block text-xs text-gray-400 dark:text-gray-500 truncate">
            {[artifact.project_name, artifact.source].filter(Boolean).join(' · ')}
          </span>
        )}
      </span>
      {artifact.share_link_count > 0 && (
        <span
          className="text-xs text-gray-400 dark:text-gray-500 shrink-0"
          title={`${artifact.share_link_count} live share link(s)`}
        >
          {artifact.share_link_count} shared
        </span>
      )}
      <StatePill state={artifact.state} />
      <span className="text-xs text-gray-400 dark:text-gray-500 w-20 text-right shrink-0">
        {timeAgo(artifact.updated_at)}
      </span>
    </div>
  );

  /**
   * Sharing lives HERE, on the shelf.
   *
   * It was reachable only from inside a briefing and a brainstorm session — so
   * a page could be shared by the API and by nothing in the UI, and this
   * screen showed "2 shared" while offering no way to reach the link. The
   * shelf is the screen whose whole job is "what have I got and where is it",
   * and a share link is the only delivery path most of these have.
   *
   * Outside the row's Link, or the button navigates instead of opening. The
   * control fetches nothing until it is opened, so one per row is cheap.
   */
  const share = SHAREABLE[artifact.kind] ? (
    <div
      className="pr-3 shrink-0"
      onClick={(e) => e.preventDefault()}
      role="presentation"
    >
      <ShareArtifactControl {...SHAREABLE[artifact.kind]!(artifact.id)} />
    </div>
  ) : null;

  if (!artifact.edit_path)
    return (
      <li className="flex items-center">
        <div className="min-w-0 flex-1">{body}</div>
        {share}
      </li>
    );
  return (
    <li className="flex items-center hover:bg-gray-50 dark:hover:bg-gray-700/50">
      {/* The shelf spans three unrelated URL trees — a page opens the builder, a
          briefing a beat, a report a room — so "back to the shelf" is never
          something the destination could work out for itself. Say it.

          And those three trees live at three HOSTS. `edit_path` comes from the
          server and can be any of them, which makes this the one link in the app
          most likely to land in the wrong shell — ShellLink decides per row. */}
      <ShellLink
        to={withReturnTo(artifact.edit_path, `${location.pathname}${location.search}`, 'Artifacts')}
        className="block min-w-0 flex-1"
      >
        {body}
      </ShellLink>
      {share}
    </li>
  );
}
